// Predictive-search combobox a11y layer: option roles/ids, aria-activedescendant, aria-expanded. Additive.

import { announce } from '@theme/ca-announcer';
import { DialogOpenEvent } from '@theme/dialog';

const ITEM_SELECTOR = '.predictive-search-results__card, [ref="resultsItems[]"]';

const LIST_SELECTOR = '.predictive-search-results__list';

const enhanced = new WeakSet();
let rootSeq = 0;

function setup(root) {
  if (enhanced.has(root)) return;
  const input = root.querySelector('input[role="combobox"]');
  const results = root.querySelector('[ref="predictiveSearchResults"]');
  if (!input || !results) return;
  enhanced.add(root);

  const uid = `ca-ps${rootSeq++}`;

  const controlsHost = (listbox) => {
    let host = listbox.nextElementSibling;
    if (!host || !host.classList.contains('ca-search-listbox-controls')) {
      host = document.createElement('div');
      host.className = 'ca-search-listbox-controls';
      listbox.after(host);
    }
    return host;
  };

  const headingForList = (list) => {
    let prev = list.previousElementSibling;
    while (prev && !prev.classList.contains('predictive-search-results__title')) {
      prev = prev.previousElementSibling;
    }
    return (
      prev ||
      list
        .closest(
          '.predictive-search-results__products, .predictive-search-results__rail, .predictive-search-results__main'
        )
        ?.querySelector('.predictive-search-results__title') ||
      null
    );
  };

  const sync = () => {
    const listbox = document.getElementById('predictive-search-results') || results;

    // Carousel slides are not listbox options: relocate the whole slideshow-component and swap the nav-set class.
    const carousels = listbox.querySelectorAll('slideshow-component');
    if (carousels.length) {
      const host = controlsHost(listbox);
      for (const carousel of carousels) {
        for (const slide of carousel.querySelectorAll('.predictive-search-results__card')) {
          slide.classList.replace('predictive-search-results__card', 'ca-search-card');
          slide.removeAttribute('role');
          slide.removeAttribute('id');
        }
        if (carousel.parentElement !== host) host.appendChild(carousel);
      }
    }

    let active = null;
    let optIndex = 0;

    for (const list of listbox.querySelectorAll(LIST_SELECTOR)) {
      // The option <ul> itself must carry role="group": axe's ownership walk stops at an implicit list.
      if (list.getAttribute('role') !== 'group') list.setAttribute('role', 'group');

      const heading = headingForList(list);
      if (heading) {
        const headingText = (heading.textContent || '').trim();
        if (headingText && !list.hasAttribute('aria-label')) {
          list.setAttribute('aria-label', headingText);
        }
        if (heading.getAttribute('role') !== 'presentation') {
          heading.setAttribute('role', 'presentation');
        }
      }

      if (list.closest('[role="tabpanel"][hidden]')) continue;

      for (const item of list.querySelectorAll(ITEM_SELECTOR)) {
        const option = item.matches('a[href], button') ? item : item.querySelector('a[href]') || item;
        if (option !== item) {
          if (item.getAttribute('role') !== 'presentation') item.setAttribute('role', 'presentation');
          if (item.id && item.id.startsWith(`${uid}-opt-`)) item.removeAttribute('id');
        }
        if (option.getAttribute('role') !== 'option') option.setAttribute('role', 'option');
        if (!option.id) option.id = `${uid}-opt-${optIndex}`;
        optIndex += 1;
        const selected = item.getAttribute('aria-selected') === 'true';
        if (option !== item && (option.getAttribute('aria-selected') === 'true') !== selected) {
          if (selected) option.setAttribute('aria-selected', 'true');
          else option.removeAttribute('aria-selected');
        }
        if (selected) active = option;
      }
    }

    const strayClear = listbox.querySelector('.predictive-search-results__clear');
    if (strayClear && listbox.parentElement) {
      const host = controlsHost(listbox);
      if (strayClear.parentElement !== host) host.appendChild(strayClear);
    }

    if (active) {
      input.setAttribute('aria-activedescendant', active.id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }

    const hasOptions = optIndex > 0;
    input.setAttribute('aria-expanded', hasOptions ? 'true' : 'false');

    if (input.getAttribute('aria-controls') !== listbox.id) input.setAttribute('aria-controls', listbox.id);
    if (input.getAttribute('aria-owns') !== listbox.id) input.setAttribute('aria-owns', listbox.id);
    if (hasOptions) {
      if (listbox.getAttribute('role') !== 'listbox') listbox.setAttribute('role', 'listbox');
      if (!listbox.hasAttribute('aria-label') && listbox.dataset.caLabel) listbox.setAttribute('aria-label', listbox.dataset.caLabel);
    } else {
      if (listbox.hasAttribute('role')) listbox.removeAttribute('role');
      const label = listbox.getAttribute('aria-label');
      if (label) {
        listbox.dataset.caLabel = label;
        listbox.removeAttribute('aria-label');
      }
    }

    if (listbox.hasAttribute('aria-expanded')) listbox.removeAttribute('aria-expanded');
  };

  const observer = new MutationObserver(sync);
  observer.observe(results, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-selected'],
  });

  sync();

  const host = root.closest('ca-search-dialog');
  const dialog = root.closest('dialog');
  if (!(host instanceof HTMLElement) || !dialog) return;
  const suggestionsLabel = host.dataset.suggestionsLabel;
  let suggestionsAnnounced = false;
  const announceSuggestions = () => {
    if (suggestionsAnnounced || !suggestionsLabel || !dialog.open || input.value) return;
    if (!root.querySelector('ca-search-discovery')) return;
    suggestionsAnnounced = true;
    announce(suggestionsLabel);
  };
  host.addEventListener(DialogOpenEvent.eventName, () => {
    suggestionsAnnounced = false;
    announceSuggestions();
  });
  new MutationObserver(announceSuggestions).observe(root, { childList: true, subtree: true });
}

function init() {
  for (const root of document.querySelectorAll('predictive-search-component')) setup(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
